import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Package,
  ShoppingBag,
  Plus,
  Pencil,
  Trash2,
  X,
  Upload,
  Image,
  Save,
  Eye,
  Search,
  LayoutDashboard,
  LogOut,
  Lock,
  User,
  TrendingUp,
  IndianRupee,
  Clock,
  CheckCircle,
  XCircle,
  Truck,
  BarChart3,
  PieChart,
  Download
} from "lucide-react";
import * as XLSX from "xlsx";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Get stored auth token
const getAuthToken = () => localStorage.getItem("adminToken");
const setAuthToken = (token) => localStorage.setItem("adminToken", token);
const clearAuthToken = () => localStorage.removeItem("adminToken");

// Axios instance with auth header
const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${getAuthToken()}` }
});

// Login Page Component
const LoginPage = ({ onLogin }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await axios.post(`${API}/admin/login`, { username, password });
      setAuthToken(response.data.token);
      onLogin();
      toast.success("Welcome back!");
    } catch (err) {
      setError("Invalid credentials");
      toast.error("Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1A2F23] flex items-center justify-center p-4" data-testid="login-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl p-8 w-full max-w-md shadow-2xl"
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-[#1A2F23] rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-[#1A2F23]">Admin Login</h1>
          <p className="text-[#9CA3AF] mt-1">Duwa Enterprises Dashboard</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#525252] mb-1">Username</label>
            <div className="relative">
              <User className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full pl-10 pr-4 py-3 rounded-lg border border-[#E5E5E5] focus:outline-none focus:ring-2 focus:ring-[#1A2F23]"
                placeholder="Enter username"
                required
                data-testid="login-username"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#525252] mb-1">Password</label>
            <div className="relative">
              <Lock className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-4 py-3 rounded-lg border border-[#E5E5E5] focus:outline-none focus:ring-2 focus:ring-[#1A2F23]"
                placeholder="Enter password"
                required
                data-testid="login-password"
              />
            </div>
          </div>
          {error && <p className="text-red-500 text-sm">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#1A2F23] text-white py-3 rounded-lg font-medium hover:bg-[#1A2F23]/90 disabled:opacity-50 transition-all"
            data-testid="login-submit"
          >
            {loading ? "Logging in..." : "Login"}
          </button>
        </form>
      </motion.div>
    </div>
  );
};

// Mini Bar Chart Component (CSS only)
const MiniBarChart = ({ data, color = "#1A2F23" }) => {
  const max = Math.max(...data.map(d => d.value), 1);
  return (
    <div className="flex items-end gap-1 h-16">
      {data.map((d, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-1">
          <div
            className="w-full rounded-t transition-all duration-500"
            style={{ height: `${(d.value / max) * 100}%`, backgroundColor: color, minHeight: d.value > 0 ? "4px" : "0" }}
          />
          <span className="text-[9px] text-[#9CA3AF] truncate w-full text-center">{d.label}</span>
        </div>
      ))}
    </div>
  );
};

// Donut Chart Component (CSS only)
const DonutChart = ({ segments, size = 120 }) => {
  const total = segments.reduce((s, seg) => s + seg.value, 0) || 1;
  let accumulated = 0;
  const gradientParts = segments.map(seg => {
    const start = (accumulated / total) * 360;
    accumulated += seg.value;
    const end = (accumulated / total) * 360;
    return `${seg.color} ${start}deg ${end}deg`;
  });
  const gradient = `conic-gradient(${gradientParts.join(", ")})`;

  return (
    <div className="flex items-center gap-4">
      <div className="relative" style={{ width: size, height: size }}>
        <div className="w-full h-full rounded-full" style={{ background: total > 0 ? gradient : "#E5E5E5" }} />
        <div className="absolute inset-3 bg-white rounded-full flex items-center justify-center">
          <span className="text-lg font-bold text-[#1A2F23]">{segments.reduce((s, seg) => s + seg.value, 0)}</span>
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        {segments.map((seg, i) => (
          <div key={i} className="flex items-center gap-2 text-xs">
            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: seg.color }} />
            <span className="text-[#525252]">{seg.label}: {seg.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Admin Dashboard Component
const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const checkAuth = async () => {
      const token = getAuthToken();
      if (!token) { setCheckingAuth(false); return; }
      try {
        await axios.get(`${API}/admin/verify`, getAuthHeaders());
        setIsAuthenticated(true);
      } catch (err) { clearAuthToken(); } finally { setCheckingAuth(false); }
    };
    checkAuth();
  }, []);

  const fetchProducts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error("Failed to fetch products:", error);
      toast.error("Failed to load products");
    }
  }, []);

  const fetchOrders = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/orders`, getAuthHeaders());
      setOrders(response.data);
    } catch (error) {
      console.error("Failed to fetch orders:", error);
      if (error.response?.status === 401) handleLogout();
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      const loadData = async () => {
        setLoading(true);
        await Promise.all([fetchProducts(), fetchOrders()]);
        setLoading(false);
      };
      loadData();
    }
  }, [isAuthenticated, fetchProducts, fetchOrders]);

  const handleLogout = () => {
    clearAuthToken();
    setIsAuthenticated(false);
    toast.success("Logged out successfully");
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;
    try {
      await axios.delete(`${API}/admin/products/${productId}`, getAuthHeaders());
      toast.success("Product deleted successfully");
      fetchProducts();
    } catch (error) {
      if (error.response?.status === 401) handleLogout();
      else toast.error("Failed to delete product");
    }
  };

  const handleDeleteOrder = async (orderId, orderNumber) => {
    if (!window.confirm(`Delete order ${orderNumber}? This action cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/orders/${orderId}`, getAuthHeaders());
      toast.success("Order deleted");
      fetchOrders();
    } catch (error) {
      if (error.response?.status === 401) handleLogout();
      else toast.error("Failed to delete order");
    }
  };

  const handleUpdateOrderStatus = async (orderId, status) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status?status=${status}`, {}, getAuthHeaders());
      toast.success(`Order status updated to ${status}`);
      fetchOrders();
    } catch (error) {
      if (error.response?.status === 401) handleLogout();
      else toast.error("Failed to update order status");
    }
  };

  // Analytics computed from orders
  const analytics = useMemo(() => {
    const now = new Date();
    const confirmedOrders = orders.filter(o => o.payment_status === "confirmed");
    const totalRevenue = confirmedOrders.reduce((s, o) => s + (o.total || 0), 0);
    const avgOrderValue = confirmedOrders.length > 0 ? totalRevenue / confirmedOrders.length : 0;

    // Orders by status
    const statusCounts = { pending: 0, processing: 0, shipped: 0, delivered: 0, cancelled: 0 };
    orders.forEach(o => { if (statusCounts[o.order_status] !== undefined) statusCounts[o.order_status]++; });

    // Revenue by last 7 days
    const dailyRevenue = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      const dateStr = d.toISOString().slice(0, 10);
      const dayLabel = d.toLocaleDateString("en", { weekday: "short" });
      const dayRevenue = confirmedOrders
        .filter(o => o.created_at?.slice(0, 10) === dateStr)
        .reduce((s, o) => s + (o.total || 0), 0);
      dailyRevenue.push({ label: dayLabel, value: dayRevenue });
    }

    // Top products by order frequency
    const productSales = {};
    orders.forEach(o => {
      o.items?.forEach(item => {
        const name = item.name || item.product_name || "Unknown";
        productSales[name] = (productSales[name] || 0) + (item.quantity || 1);
      });
    });
    const topProducts = Object.entries(productSales)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([name, qty]) => ({ name, qty }));

    // Recent orders (last 5)
    const recentOrders = [...orders].slice(0, 5);

    return { totalRevenue, avgOrderValue, statusCounts, dailyRevenue, topProducts, recentOrders };
  }, [orders]);

  const stats = {
    totalProducts: products.length,
    totalOrders: orders.length,
    pendingOrders: orders.filter(o => o.order_status === "pending").length,
    totalRevenue: analytics.totalRevenue
  };

  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-[#F9F9F7] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#1A2F23]"></div>
      </div>
    );
  }

  if (!isAuthenticated) return <LoginPage onLogin={() => setIsAuthenticated(true)} />;

  const filteredProducts = products.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredOrders = orders.filter(o =>
    o.order_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    o.customer?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const exportOrdersToExcel = () => {
    const data = filteredOrders.map(order => ({
      "Order ID": order.order_number || "",
      "Date": order.created_at?.slice(0, 10) || "",
      "Customer Name": order.customer?.name || "",
      "Email": order.customer?.email || "",
      "Phone": order.customer?.phone || "",
      "Address": order.customer?.address || "",
      "Items": order.items?.map(i => `${i.name} x${i.quantity}`).join(", ") || "",
      "Total (INR)": order.total || 0,
      "Payment Status": order.payment_status || "",
      "Order Status": order.order_status || "",
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    ws["!cols"] = [{ wch: 22 }, { wch: 12 }, { wch: 20 }, { wch: 25 }, { wch: 15 }, { wch: 30 }, { wch: 35 }, { wch: 12 }, { wch: 14 }, { wch: 14 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Orders");
    XLSX.writeFile(wb, `Duwa_Orders_${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success("Orders exported successfully!");
  };

  const statusColors = {
    pending: "bg-yellow-100 text-yellow-700",
    processing: "bg-blue-100 text-blue-700",
    shipped: "bg-purple-100 text-purple-700",
    delivered: "bg-green-100 text-green-700",
    cancelled: "bg-red-100 text-red-700",
    confirmed: "bg-green-100 text-green-700",
    payment_verification: "bg-amber-100 text-amber-700",
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7]" data-testid="admin-dashboard">
      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 bg-[#1A2F23] min-h-screen fixed left-0 top-0 p-6">
          <div className="mb-10">
            <h1 className="text-2xl font-bold text-white">Admin Panel</h1>
            <p className="text-[#8FA893] text-sm">Duwa Enterprises</p>
          </div>

          <nav className="space-y-2">
            <button
              onClick={() => setActiveTab("dashboard")}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === "dashboard" ? "bg-white/10 text-white" : "text-[#8FA893] hover:bg-white/5"
              }`}
              data-testid="nav-dashboard"
            >
              <LayoutDashboard className="w-5 h-5" />
              Dashboard
            </button>
            <button
              onClick={() => setActiveTab("products")}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === "products" ? "bg-white/10 text-white" : "text-[#8FA893] hover:bg-white/5"
              }`}
              data-testid="nav-products"
            >
              <Package className="w-5 h-5" />
              Products
            </button>
            <button
              onClick={() => setActiveTab("orders")}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === "orders" ? "bg-white/10 text-white" : "text-[#8FA893] hover:bg-white/5"
              }`}
              data-testid="nav-orders"
            >
              <ShoppingBag className="w-5 h-5" />
              Orders
              {stats.pendingOrders > 0 && (
                <span className="ml-auto bg-yellow-400 text-black text-xs px-2 py-0.5 rounded-full">
                  {stats.pendingOrders}
                </span>
              )}
            </button>
          </nav>

          <div className="absolute bottom-6 left-6 right-6 space-y-3">
            <a href="/" className="flex items-center gap-2 text-[#8FA893] hover:text-white transition-colors text-sm">
              <Eye className="w-4 h-4" /> View Store
            </a>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-red-400 hover:text-red-300 transition-colors text-sm w-full"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 ml-64 p-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl font-bold text-[#1A2F23] capitalize">{activeTab}</h2>
              <p className="text-[#525252]">
                {activeTab === "products" && `${products.length} products`}
                {activeTab === "orders" && `${orders.length} orders`}
                {activeTab === "dashboard" && "Business overview & analytics"}
              </p>
            </div>
            <div className="flex items-center gap-4">
              {activeTab !== "dashboard" && (
                <div className="relative">
                  <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 rounded-lg border border-[#E5E5E5] focus:outline-none focus:ring-2 focus:ring-[#1A2F23] w-64"
                    data-testid="search-input"
                  />
                </div>
              )}
              {activeTab === "products" && (
                <button
                  onClick={() => { setEditingProduct(null); setShowProductModal(true); }}
                  className="flex items-center gap-2 bg-[#1A2F23] text-white px-4 py-2 rounded-lg hover:bg-[#1A2F23]/90 transition-colors"
                  data-testid="add-product-btn"
                >
                  <Plus className="w-5 h-5" /> Add Product
                </button>
              )}
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#1A2F23]"></div>
            </div>
          ) : (
            <>
              {/* ===== DASHBOARD TAB ===== */}
              {activeTab === "dashboard" && (
                <div className="space-y-6" data-testid="dashboard-analytics">
                  {/* Stats Row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0 }}
                      className="bg-white p-5 rounded-xl border border-[#E5E5E5] hover:shadow-md transition-shadow">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm text-[#9CA3AF]">Total Revenue</span>
                        <div className="w-9 h-9 bg-emerald-50 rounded-lg flex items-center justify-center">
                          <IndianRupee className="w-4 h-4 text-emerald-600" />
                        </div>
                      </div>
                      <p className="text-2xl font-bold text-[#1A2F23]" data-testid="stat-revenue">
                        {stats.totalRevenue.toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 })}
                      </p>
                      <p className="text-xs text-[#9CA3AF] mt-1">From confirmed orders</p>
                    </motion.div>

                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
                      className="bg-white p-5 rounded-xl border border-[#E5E5E5] hover:shadow-md transition-shadow">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm text-[#9CA3AF]">Total Orders</span>
                        <div className="w-9 h-9 bg-blue-50 rounded-lg flex items-center justify-center">
                          <ShoppingBag className="w-4 h-4 text-blue-600" />
                        </div>
                      </div>
                      <p className="text-2xl font-bold text-[#1A2F23]" data-testid="stat-orders">{stats.totalOrders}</p>
                      <p className="text-xs text-[#9CA3AF] mt-1">All time</p>
                    </motion.div>

                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
                      className="bg-white p-5 rounded-xl border border-[#E5E5E5] hover:shadow-md transition-shadow">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm text-[#9CA3AF]">Pending Orders</span>
                        <div className="w-9 h-9 bg-amber-50 rounded-lg flex items-center justify-center">
                          <Clock className="w-4 h-4 text-amber-600" />
                        </div>
                      </div>
                      <p className="text-2xl font-bold text-amber-600" data-testid="stat-pending">{stats.pendingOrders}</p>
                      <p className="text-xs text-[#9CA3AF] mt-1">Needs attention</p>
                    </motion.div>

                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
                      className="bg-white p-5 rounded-xl border border-[#E5E5E5] hover:shadow-md transition-shadow">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm text-[#9CA3AF]">Avg Order Value</span>
                        <div className="w-9 h-9 bg-violet-50 rounded-lg flex items-center justify-center">
                          <TrendingUp className="w-4 h-4 text-violet-600" />
                        </div>
                      </div>
                      <p className="text-2xl font-bold text-[#1A2F23]" data-testid="stat-avg-order">
                        {analytics.avgOrderValue.toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 })}
                      </p>
                      <p className="text-xs text-[#9CA3AF] mt-1">Per confirmed order</p>
                    </motion.div>
                  </div>

                  {/* Charts Row */}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                    {/* Revenue Chart */}
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
                      className="lg:col-span-2 bg-white p-6 rounded-xl border border-[#E5E5E5]">
                      <div className="flex items-center gap-2 mb-5">
                        <BarChart3 className="w-5 h-5 text-[#8FA893]" />
                        <h3 className="font-semibold text-[#1A2F23]">Revenue (Last 7 Days)</h3>
                      </div>
                      <div className="h-44 flex items-end gap-3">
                        {analytics.dailyRevenue.map((d, i) => {
                          const max = Math.max(...analytics.dailyRevenue.map(x => x.value), 1);
                          const pct = (d.value / max) * 100;
                          return (
                            <div key={i} className="flex-1 flex flex-col items-center gap-2">
                              <span className="text-[10px] text-[#9CA3AF] font-medium">
                                {d.value > 0 ? `${(d.value/1000).toFixed(1)}k` : ""}
                              </span>
                              <div className="w-full bg-[#F5F5F4] rounded-t-lg overflow-hidden" style={{ height: "120px" }}>
                                <div
                                  className="w-full bg-gradient-to-t from-[#1A2F23] to-[#3D6B4F] rounded-t-lg transition-all duration-700"
                                  style={{ height: `${Math.max(pct, d.value > 0 ? 8 : 0)}%`, marginTop: "auto", position: "relative", top: `${100 - Math.max(pct, d.value > 0 ? 8 : 0)}%` }}
                                />
                              </div>
                              <span className="text-xs text-[#525252] font-medium">{d.label}</span>
                            </div>
                          );
                        })}
                      </div>
                    </motion.div>

                    {/* Order Status Donut */}
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
                      className="bg-white p-6 rounded-xl border border-[#E5E5E5]">
                      <div className="flex items-center gap-2 mb-5">
                        <PieChart className="w-5 h-5 text-[#8FA893]" />
                        <h3 className="font-semibold text-[#1A2F23]">Order Status</h3>
                      </div>
                      <DonutChart segments={[
                        { label: "Pending", value: analytics.statusCounts.pending, color: "#F59E0B" },
                        { label: "Processing", value: analytics.statusCounts.processing, color: "#3B82F6" },
                        { label: "Shipped", value: analytics.statusCounts.shipped, color: "#8B5CF6" },
                        { label: "Delivered", value: analytics.statusCounts.delivered, color: "#10B981" },
                        { label: "Cancelled", value: analytics.statusCounts.cancelled, color: "#EF4444" },
                      ]} />
                    </motion.div>
                  </div>

                  {/* Bottom Row */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                    {/* Top Products */}
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
                      className="bg-white p-6 rounded-xl border border-[#E5E5E5]">
                      <h3 className="font-semibold text-[#1A2F23] mb-4">Top Selling Products</h3>
                      {analytics.topProducts.length === 0 ? (
                        <p className="text-sm text-[#9CA3AF] text-center py-6">No sales data yet</p>
                      ) : (
                        <div className="space-y-3">
                          {analytics.topProducts.map((p, i) => {
                            const maxQty = analytics.topProducts[0]?.qty || 1;
                            return (
                              <div key={i} className="flex items-center gap-3">
                                <span className="w-6 h-6 bg-[#F5F5F4] rounded-full flex items-center justify-center text-xs font-bold text-[#525252]">
                                  {i + 1}
                                </span>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-[#1A2F23] truncate">{p.name}</p>
                                  <div className="mt-1 h-1.5 bg-[#F5F5F4] rounded-full overflow-hidden">
                                    <div className="h-full bg-[#8FA893] rounded-full transition-all duration-500"
                                      style={{ width: `${(p.qty / maxQty) * 100}%` }} />
                                  </div>
                                </div>
                                <span className="text-sm font-semibold text-[#1A2F23] tabular-nums">{p.qty} sold</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </motion.div>

                    {/* Recent Orders */}
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
                      className="bg-white p-6 rounded-xl border border-[#E5E5E5]">
                      <h3 className="font-semibold text-[#1A2F23] mb-4">Recent Orders</h3>
                      {analytics.recentOrders.length === 0 ? (
                        <p className="text-sm text-[#9CA3AF] text-center py-6">No orders yet</p>
                      ) : (
                        <div className="space-y-3">
                          {analytics.recentOrders.map((o, i) => (
                            <div key={i} className="flex items-center justify-between py-2 border-b border-[#F5F5F4] last:border-0">
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-[#1A2F23] truncate">{o.customer?.name || "Guest"}</p>
                                <p className="text-xs text-[#9CA3AF]">{o.order_number}</p>
                              </div>
                              <div className="text-right flex-shrink-0 ml-3">
                                <p className="text-sm font-semibold text-[#1A2F23]">
                                  {(o.total || 0).toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 })}
                                </p>
                                <span className={`text-[10px] px-2 py-0.5 rounded-full ${statusColors[o.order_status] || "bg-gray-100 text-gray-600"}`}>
                                  {o.order_status}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </motion.div>
                  </div>

                  {/* Products Count */}
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
                    className="bg-[#1A2F23] p-6 rounded-xl text-white flex items-center justify-between">
                    <div>
                      <p className="text-[#8FA893] text-sm">Product Catalog</p>
                      <p className="text-3xl font-bold mt-1">{stats.totalProducts} Products</p>
                      <p className="text-[#8FA893] text-sm mt-1">Live on your store</p>
                    </div>
                    <button onClick={() => setActiveTab("products")}
                      className="bg-white/10 hover:bg-white/20 text-white px-5 py-2.5 rounded-lg text-sm font-medium transition-colors">
                      Manage Products
                    </button>
                  </motion.div>
                </div>
              )}

              {/* ===== PRODUCTS TAB ===== */}
              {activeTab === "products" && (
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-[#F5F5F4]">
                      <tr>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#525252]">Product</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#525252]">Category</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#525252]">Price</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#525252]">Images</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#525252]">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E5E5E5]">
                      {filteredProducts.map((product) => (
                        <tr key={product.id} className="hover:bg-[#F9F9F7]" data-testid={`product-row-${product.id}`}>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-4">
                              <img src={product.image_url} alt={product.name} className="w-12 h-12 object-cover rounded-lg" />
                              <div>
                                <p className="font-medium text-[#1A1A1A]">{product.name}</p>
                                <p className="text-sm text-[#9CA3AF] truncate max-w-xs">{product.short_description}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="px-3 py-1 bg-[#8FA893]/20 text-[#1A2F23] rounded-full text-sm">{product.category}</span>
                          </td>
                          <td className="px-6 py-4">
                            <div>
                              <p className="font-medium text-[#1A2F23]">{(product.price || 0).toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 })}</p>
                              {product.original_price > product.price && (
                                <p className="text-sm text-[#9CA3AF] line-through">{(product.original_price || 0).toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 })}</p>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-1">
                              {product.images?.slice(0, 3).map((img, idx) => (
                                <img key={idx} src={img} alt="" className="w-8 h-8 object-cover rounded" />
                              ))}
                              {product.images?.length > 3 && (
                                <span className="text-sm text-[#9CA3AF]">+{product.images.length - 3}</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => { setEditingProduct(product); setShowProductModal(true); }}
                                className="p-2 hover:bg-[#F5F5F4] rounded-lg transition-colors"
                                data-testid={`edit-product-${product.id}`}
                              >
                                <Pencil className="w-4 h-4 text-[#525252]" />
                              </button>
                              <button
                                onClick={() => handleDeleteProduct(product.id)}
                                className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                                data-testid={`delete-product-${product.id}`}
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredProducts.length === 0 && (
                    <div className="text-center py-12 text-[#9CA3AF]">No products found</div>
                  )}
                </div>
              )}

              {/* ===== ORDERS TAB ===== */}
              {activeTab === "orders" && (
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                  <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E5E5]">
                    <p className="text-sm text-[#525252]">{filteredOrders.length} orders</p>
                    <button
                      onClick={exportOrdersToExcel}
                      className="flex items-center gap-2 px-4 py-2 bg-[#1A2F23] text-white text-sm font-medium rounded-lg hover:bg-[#2A4033] transition-colors"
                      data-testid="export-orders-excel-btn"
                    >
                      <Download className="w-4 h-4" />
                      Export to Excel
                    </button>
                  </div>
                  <table className="w-full">
                    <thead className="bg-[#F5F5F4]">
                      <tr>
                        <th className="px-5 py-4 text-left text-sm font-medium text-[#525252]">Order</th>
                        <th className="px-5 py-4 text-left text-sm font-medium text-[#525252]">Customer</th>
                        <th className="px-5 py-4 text-left text-sm font-medium text-[#525252]">Email</th>
                        <th className="px-5 py-4 text-left text-sm font-medium text-[#525252]">Items</th>
                        <th className="px-5 py-4 text-left text-sm font-medium text-[#525252]">Total</th>
                        <th className="px-5 py-4 text-left text-sm font-medium text-[#525252]">Payment</th>
                        <th className="px-5 py-4 text-left text-sm font-medium text-[#525252]">Status</th>
                        <th className="px-5 py-4 text-left text-sm font-medium text-[#525252]">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E5E5E5]">
                      {filteredOrders.map((order) => (
                        <tr key={order.id} className="hover:bg-[#F9F9F7]" data-testid={`order-row-${order.id}`}>
                          <td className="px-5 py-4">
                            <p className="font-medium text-[#1A2F23] text-sm">{order.order_number}</p>
                            <p className="text-xs text-[#9CA3AF]">{order.created_at?.slice(0, 10)}</p>
                          </td>
                          <td className="px-5 py-4">
                            <p className="font-medium text-[#1A1A1A] text-sm">{order.customer?.name}</p>
                            <p className="text-xs text-[#9CA3AF]">{order.customer?.phone}</p>
                          </td>
                          <td className="px-5 py-4">
                            <p className="text-sm text-[#525252]">{order.customer?.email || "-"}</p>
                          </td>
                          <td className="px-5 py-4">
                            <p className="text-[#525252] text-sm">{order.items?.length} items</p>
                          </td>
                          <td className="px-5 py-4">
                            <p className="font-medium text-[#1A2F23] text-sm">
                              {(order.total || 0).toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 })}
                            </p>
                          </td>
                          <td className="px-5 py-4">
                            <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusColors[order.payment_status] || "bg-gray-100 text-gray-600"}`}>
                              {order.payment_status}
                            </span>
                          </td>
                          <td className="px-5 py-4">
                            <select
                              value={order.order_status}
                              onChange={(e) => handleUpdateOrderStatus(order.id, e.target.value)}
                              className="px-2.5 py-1 rounded-lg border border-[#E5E5E5] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1A2F23]"
                              data-testid={`order-status-${order.id}`}
                            >
                              <option value="pending">Pending</option>
                              <option value="processing">Processing</option>
                              <option value="shipped">Shipped</option>
                              <option value="delivered">Delivered</option>
                              <option value="cancelled">Cancelled</option>
                            </select>
                          </td>
                          <td className="px-5 py-4">
                            <button
                              onClick={() => handleDeleteOrder(order.id, order.order_number)}
                              className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                              data-testid={`delete-order-${order.id}`}
                              title="Delete order"
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredOrders.length === 0 && (
                    <div className="text-center py-12 text-[#9CA3AF]">No orders found</div>
                  )}
                </div>
              )}
            </>
          )}
        </main>
      </div>

      {/* Product Modal */}
      <AnimatePresence>
        {showProductModal && (
          <ProductModal
            product={editingProduct}
            onClose={() => { setShowProductModal(false); setEditingProduct(null); }}
            onSave={() => { fetchProducts(); setShowProductModal(false); setEditingProduct(null); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// Product Modal Component
const ProductModal = ({ product, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: product?.name || "",
    description: product?.description || "",
    short_description: product?.short_description || "",
    price: product?.price || "",
    original_price: product?.original_price || "",
    discount_text: product?.discount_text || "",
    category: product?.category || "Hair Oil",
    image_url: product?.image_url || "",
    images: product?.images || [],
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const uploadData = new FormData();
      uploadData.append("file", file);
      const response = await axios.post(`${API}/admin/upload`, uploadData, getAuthHeaders());
      const imageUrl = response.data.url;
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, imageUrl],
        image_url: prev.image_url || imageUrl
      }));
      toast.success("Image uploaded successfully");
    } catch (error) {
      console.error("Upload failed:", error);
      toast.error("Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  const setMainImage = (imageUrl) => {
    setFormData(prev => ({ ...prev, image_url: imageUrl }));
  };

  const removeImage = (index) => {
    setFormData(prev => {
      const newImages = prev.images.filter((_, i) => i !== index);
      const newMainImage = prev.image_url === prev.images[index]
        ? (newImages[0] || "")
        : prev.image_url;
      return { ...prev, images: newImages, image_url: newMainImage };
    });
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.price) {
      toast.error("Name and price are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...formData,
        price: parseFloat(formData.price),
        original_price: parseFloat(formData.original_price) || parseFloat(formData.price),
      };
      if (product) {
        await axios.put(`${API}/admin/products/${product.id}`, payload, getAuthHeaders());
        toast.success("Product updated");
      } else {
        await axios.post(`${API}/admin/products`, payload, getAuthHeaders());
        toast.success("Product created");
      }
      onSave();
    } catch (error) {
      console.error("Save failed:", error);
      toast.error("Failed to save product");
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
      data-testid="product-modal"
    >
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.95 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-[#1A2F23]">
            {product ? "Edit Product" : "Add New Product"}
          </h3>
          <button onClick={onClose} className="p-2 hover:bg-[#F5F5F4] rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#525252] mb-1">Product Name *</label>
            <input name="name" value={formData.name} onChange={handleChange}
              className="w-full px-4 py-2 rounded-lg border border-[#E5E5E5] focus:outline-none focus:ring-2 focus:ring-[#1A2F23]"
              data-testid="input-product-name" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#525252] mb-1">Price *</label>
              <input name="price" type="number" value={formData.price} onChange={handleChange}
                className="w-full px-4 py-2 rounded-lg border border-[#E5E5E5] focus:outline-none focus:ring-2 focus:ring-[#1A2F23]"
                data-testid="input-product-price" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#525252] mb-1">Original Price</label>
              <input name="original_price" type="number" value={formData.original_price} onChange={handleChange}
                className="w-full px-4 py-2 rounded-lg border border-[#E5E5E5] focus:outline-none focus:ring-2 focus:ring-[#1A2F23]" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#525252] mb-1">Category</label>
              <select name="category" value={formData.category} onChange={handleChange}
                className="w-full px-4 py-2 rounded-lg border border-[#E5E5E5] focus:outline-none focus:ring-2 focus:ring-[#1A2F23]">
                <option>Hair Oil</option>
                <option>Shampoo</option>
                <option>Herbal Powder</option>
                <option>Hair Serum</option>
                <option>Combo Pack</option>
                <option>Tools</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#525252] mb-1">Discount Text</label>
              <input name="discount_text" value={formData.discount_text} onChange={handleChange}
                className="w-full px-4 py-2 rounded-lg border border-[#E5E5E5] focus:outline-none focus:ring-2 focus:ring-[#1A2F23]"
                placeholder="e.g. Flat 25% OFF" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#525252] mb-1">Short Description</label>
            <input name="short_description" value={formData.short_description} onChange={handleChange}
              className="w-full px-4 py-2 rounded-lg border border-[#E5E5E5] focus:outline-none focus:ring-2 focus:ring-[#1A2F23]" />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#525252] mb-1">Full Description</label>
            <textarea name="description" value={formData.description} onChange={handleChange} rows={3}
              className="w-full px-4 py-2 rounded-lg border border-[#E5E5E5] focus:outline-none focus:ring-2 focus:ring-[#1A2F23]" />
          </div>

          {/* Image Gallery */}
          <div>
            <label className="block text-sm font-medium text-[#525252] mb-2">Product Images</label>
            <div className="flex flex-wrap gap-3 mb-3">
              {formData.images.map((img, idx) => (
                <div key={idx} className="relative group w-20 h-20">
                  <img src={img} alt="" className={`w-full h-full object-cover rounded-lg border-2 ${
                    img === formData.image_url ? "border-[#1A2F23]" : "border-[#E5E5E5]"
                  }`} />
                  {img === formData.image_url && (
                    <span className="absolute -top-1 -left-1 bg-[#1A2F23] text-white text-[8px] px-1.5 py-0.5 rounded">Main</span>
                  )}
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-1">
                    <button onClick={() => setMainImage(img)} className="p-1 bg-white rounded text-[#1A2F23]" title="Set as main">
                      <Eye className="w-3 h-3" />
                    </button>
                    <button onClick={() => removeImage(idx)} className="p-1 bg-white rounded text-red-500" title="Remove">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))}
              <label className="w-20 h-20 border-2 border-dashed border-[#E5E5E5] rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-[#8FA893] hover:bg-[#F9F9F7] transition-colors">
                <Upload className="w-5 h-5 text-[#9CA3AF]" />
                <span className="text-[9px] text-[#9CA3AF] mt-1">{uploading ? "..." : "Upload"}</span>
                <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploading} />
              </label>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-[#E5E5E5]">
          <button onClick={onClose} className="px-5 py-2 rounded-lg border border-[#E5E5E5] text-[#525252] hover:bg-[#F5F5F4] transition-colors">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2 rounded-lg bg-[#1A2F23] text-white hover:bg-[#1A2F23]/90 disabled:opacity-50 transition-colors"
            data-testid="save-product-btn"
          >
            <Save className="w-4 h-4" />
            {saving ? "Saving..." : "Save Product"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default AdminDashboard;
